import { apiClient } from './api-client';

/**
 * B6.14 — staff-side Customer Portal access management.
 *
 * Note the response shape: `apiClient` already unwraps the `{ data, meta }` envelope
 * in its response interceptor, so `r.data` here is the payload itself. Do NOT reach
 * for `r.data.data` — that is the double-unwrap this codebase documents against.
 */

export type PortalAccessState = 'NOT_ENABLED' | 'INVITATION_PENDING' | 'ACTIVE';

export type CustomerPortalRoleSlug =
  'customer-owner' | 'customer-manager' | 'customer-staff' | 'customer-readonly';

export const CUSTOMER_PORTAL_ROLES: {
  slug: CustomerPortalRoleSlug;
  label: string;
  hint: string;
}[] = [
  {
    slug: 'customer-readonly',
    label: 'Read-Only',
    hint: 'View jobs, equipment and reports. Cannot approve or reject.',
  },
  {
    slug: 'customer-staff',
    label: 'Staff',
    hint: 'Read-only access plus document downloads.',
  },
  {
    slug: 'customer-manager',
    label: 'Manager',
    hint: 'Can approve or reject quotations and authorizations.',
  },
  {
    slug: 'customer-owner',
    label: 'Owner',
    hint: 'Full portal access for this company.',
  },
];

export interface PortalAccessStatus {
  contactId: string;
  state: PortalAccessState;
  isPortalUser: boolean;
  username: string | null;
  email: string | null;
  roleSlug: string | null;
  lastLoginAt: string | null;
  passwordChangedAt: string | null;
  invitationExpiresAt: string | null;
  canInvite: boolean;
}

export interface PortalInvitationResult {
  contactId: string;
  expiresAt: string;
  sent: boolean;
}

const base = '/customers';
const path = (customerId: string, contactId: string) =>
  `${base}/${customerId}/contacts/${contactId}/portal`;

export const portalAccessService = {
  getStatus: (customerId: string, contactId: string): Promise<PortalAccessStatus> =>
    apiClient.get<PortalAccessStatus>(path(customerId, contactId)).then((r) => r.data),

  enable: (
    customerId: string,
    contactId: string,
    payload: { roleSlug?: CustomerPortalRoleSlug; sendInvitation?: boolean },
  ): Promise<PortalAccessStatus> =>
    apiClient
      .post<PortalAccessStatus>(`${path(customerId, contactId)}/enable`, payload)
      .then((r) => r.data),

  disable: (
    customerId: string,
    contactId: string,
    payload: { reason?: string } = {},
  ): Promise<PortalAccessStatus> =>
    apiClient
      .post<PortalAccessStatus>(`${path(customerId, contactId)}/disable`, payload)
      .then((r) => r.data),

  resendInvitation: (customerId: string, contactId: string): Promise<PortalInvitationResult> =>
    apiClient
      .post<PortalInvitationResult>(`${path(customerId, contactId)}/invitation`)
      .then((r) => r.data),
};
